import { css } from '@compiled/react';
import React, { useCallback } from 'react';
import { useLoaderData, useLocation, useNavigate } from 'react-router-dom';
import { LessonsList, useDeviceSize } from '@atlassian/learning-components';
import { token } from '@atlaskit/tokens';

import { CourseLoaderProps } from 'src/pages/CoursePage/loader';
import { getLessonsData } from 'src/pages/CoursePage/utils';
import { ContentfulSmCourse } from 'src/graphql/queries/GetSmCourse';
import { getSlug } from 'src/utils/slug';
import { useLessonProgress } from 'src/store/progress/progressStore';
import { LearningProgressStatus } from 'src/services/http/progress';
import { useAtlassianUser } from 'src/hooks/useAtlassianUser';
import { sendTrackEvent } from 'src/utils/analytics';
import { CUSTOM_TABLET_WIDTH } from 'src/pages/LearningPathPage/constants';
import { getLessonUrl, withPrefix } from 'src/utils/getUrl';
import { getLearningPathsHubPart } from 'src/utils/pathUtils';

export const LessonListWrapper: React.FC = () => {
  const { course, learningPath } = useLoaderData() as CourseLoaderProps;
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isLoading } = useAtlassianUser();
  const [{ progressForLessons, isLoadingProgressForLessons }] = useLessonProgress();
  const deviceWidth = useDeviceSize();
  const isTablet = deviceWidth >= CUSTOM_TABLET_WIDTH;

  const onLessonItemClick = useCallback(
    (lesson: ContentfulSmCourse['lessonsCollection']['items'][0]): void => {
      if (course) {
        const courseSlug = getSlug(course);

        const url = withPrefix(
          getLessonUrl({
            lessonSlug: getSlug(lesson),
            courseSlug,
            pathSlug: learningPath?.slug,
          }),
          getLearningPathsHubPart(location.pathname),
        );

        const hasNotStartedCourse =
          !progressForLessons?.overallStatus || progressForLessons.overallStatus === LearningProgressStatus.NOT_STARTED;

        if (hasNotStartedCourse) {
          sendTrackEvent(
            'learningApp',
            'course',
            'started',
            {
              courseSlug,
            },
            user?.account_id,
          );
        }

        navigate(url);
      }
    },
    [course, navigate, learningPath, location.pathname, progressForLessons, user],
  );

  const getLessonCompletionStatus = (lessonId: string): boolean => {
    if (isLoading || isLoadingProgressForLessons || !progressForLessons || !user) {
      return false;
    }

    const lessonProgress = progressForLessons.lessons.find((p) => p.lessonId === lessonId);

    return lessonProgress?.status === LearningProgressStatus.COMPLETED;
  };

  const lessons = getLessonsData(course, getLessonCompletionStatus);

  const handleLessonClick = useCallback(
    (slug: string): void => {
      const lesson = course.lessonsCollection.items.find((l) => l.slug === slug);

      if (lesson) {
        onLessonItemClick(lesson);
      }
    },
    [course, onLessonItemClick],
  );

  return (
    <div id="lesson-list-wrapper" css={lessonListContainerStyles} data-testid="lesson-list-wrapper">
      <LessonsList lessons={lessons} onLessonClick={handleLessonClick} isStandalone={true} isTablet={isTablet} />
    </div>
  );
};

const lessonListContainerStyles = css({
  display: 'flex',
  flexDirection: 'column',
  gap: token('space.200'),
  alignSelf: 'stretch',
  width: '100%',
});
